import SingleDate from '@/components/date/SingleDate';
import CategorySelector from '@/modules/cashFlow/components/CategorySelector';
import { useDeleteTransactionMutation } from '@/store/transactionsApi';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import dayjs from '@/config/dayjs';
import React, { useEffect, useState } from 'react';

// Category selection handled by CategorySelector

interface BulkEditTransactionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (updates: {
    description?: string;
    categoryId?: number;
    date?: string;
  }) => void;
  count: number; // Number of transactions being edited
  isSaving: boolean; // Loading state from parent
  transactionIds: number[]; // Add prop for IDs
  investorId?: number;
}

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 400, md: 450 },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

const BulkEditTransactionModal: React.FC<BulkEditTransactionModalProps> = ({
  open,
  onClose,
  onSave,
  count,
  isSaving,
  transactionIds,
  investorId,
}) => {
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<string>(''); // Use string for Select value
  const [date, setDate] = useState<string | null>(null); // YYYY-MM-DD format or null
  const [deleteError, setDeleteError] = useState<string | null>(null); // State for delete error
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] =
    useState(false); // State for confirm dialog

  // Category fetching and UI are handled by CategorySelector

  // Delete mutation hook
  const [deleteTransaction] = useDeleteTransactionMutation();
  const [isDeleting, setIsDeleting] = useState(false); // Overall deleting state

  // Reset state when modal opens/closes or count changes
  useEffect(() => {
    if (open) {
      setDescription('');
      setCategoryId('');
      setDate(null);
    }
  }, [open]);

  const handleSave = () => {
    const updates: {
      description?: string;
      categoryId?: number;
      date?: string;
    } = {};
    if (description.trim()) {
      updates.description = description.trim();
    }
    if (categoryId) {
      updates.categoryId = parseInt(categoryId);
    }
    if (date) {
      // Ensure date is in ISO format for the API
      updates.date = `${date}T00:00:00.000Z`;
    }

    // Only call save if at least one field has a value
    if (Object.keys(updates).length > 0) {
      onSave(updates);
    } else {
      // Optionally provide feedback that no changes were made
      onClose();
    }
  };

  // Opens the confirmation dialog
  const handleDelete = async () => {
    setIsConfirmDeleteDialogOpen(true);
  };

  // Performs the actual deletion after confirmation
  const handleConfirmDelete = async () => {
    setIsConfirmDeleteDialogOpen(false); // Close the confirm dialog
    setDeleteError(null); // Reset previous errors
    setIsDeleting(true); // Start overall deleting process
    console.log(
      `[Bulk Delete] Starting sequential deletion for ${transactionIds.length} IDs:`,
      transactionIds,
    );
    const failedDeletes: { id: number; error: any }[] = [];

    try {
      for (const id of transactionIds) {
        try {
          console.log(`[Bulk Delete] Attempting delete for ID: ${id}`);
          await deleteTransaction(id.toString()).unwrap(); // Await each delete individually
          console.log(`[Bulk Delete] Successfully deleted ID: ${id}`);
        } catch (err: any) {
          console.error(`[Bulk Delete] Failed to delete ID: ${id}`, err);
          failedDeletes.push({ id: id, error: err });
          // Decide if we should continue or stop on first error?
          // For now, let's continue and report all failures at the end.
        }
      }

      console.log('[Bulk Delete] Sequential deletion process finished.');

      if (failedDeletes.length > 0) {
        console.error('Some deletions failed:', failedDeletes);
        // Try to get a meaningful error message from the first failure
        let errorMsg = 'Some transactions could not be deleted.';
        const firstFailureError = failedDeletes[0]?.error;
        if (firstFailureError) {
          const rtkError = firstFailureError as FetchBaseQueryError; // Type assertion
          // Try to extract message from data property
          if (typeof rtkError.data === 'string') {
            errorMsg = rtkError.data;
          } else if (
            typeof rtkError.data === 'object' &&
            rtkError.data !== null &&
            'message' in rtkError.data &&
            typeof rtkError.data.message === 'string'
          ) {
            errorMsg = rtkError.data.message;
          } else if (firstFailureError.message) {
            errorMsg = firstFailureError.message; // Fallback to general error message
          }
        }
        setDeleteError(`(${failedDeletes.length}/${count}) ${errorMsg}`);
      } else {
        console.log(`Successfully deleted ${count} transactions sequentially.`);
        onClose(); // Close modal only if all deletions succeed
      }
    } catch (error) {
      // This catch block might be less likely to be hit now, but good practice
      console.error(
        '[Bulk Delete] Unexpected error during sequential delete loop:',
        error,
      );
      setDeleteError(
        'An unexpected error occurred while processing deletions.',
      );
    } finally {
      setIsDeleting(false); // End overall deleting process regardless of outcome
    }
  };

  const handleDateChange = (newDate: string | null) => {
    // Convert from MM/DD/YYYY to YYYY-MM-DD format for internal state
    if (newDate) {
      const formattedDate = dayjs(newDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      setDate(formattedDate);
    } else {
      setDate(null);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='bulk-edit-transaction-modal-title'
      >
        <Box sx={style}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography
              id='bulk-edit-transaction-modal-title'
              variant='h6'
              component='h2'
            >
              Edit {count} Transaction{count > 1 ? 's' : ''}
            </Typography>
            <IconButton
              onClick={onClose}
              size='small'
              disabled={isSaving}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <TextField
            id='description'
            label='Description (Optional)'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            variant='outlined'
            size='small'
            disabled={isSaving}
          />

          <CategorySelector
            label={'Category (Optional)'}
            value={categoryId}
            onChange={(id) => setCategoryId(id as any)}
            disabled={isSaving}
            allowNoneOption
            investorId={investorId}
          />

          <Box sx={{ width: '100%' }}>
            <SingleDate
              date={date ? dayjs(date).format('MM/DD/YYYY') : ''}
              onChange={handleDateChange}
              label='Date (Optional)'
              showShortcuts={false}
              maxDate={dayjs()}
            />
          </Box>

          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{ mt: 2 }}
          >
            <Button
              size='large'
              variant='contained'
              color='error'
              onClick={handleDelete}
              disabled={isSaving || isDeleting}
            >
              {isDeleting ? (
                <CircularProgress
                  size={24}
                  color='inherit'
                />
              ) : (
                `Delete (${count})`
              )}
            </Button>
            <Stack
              direction='row'
              spacing={1}
            >
              <Button
                size='large'
                variant='outlined'
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                size='large'
                variant='contained'
                onClick={handleSave}
                disabled={
                  isSaving ||
                  Object.keys({ description, categoryId, date }).filter(
                    (k) => !!{ description, categoryId, date }[k],
                  ).length === 0
                }
              >
                {isSaving ? <CircularProgress size={24} /> : 'Save'}
              </Button>
            </Stack>
          </Stack>
          {deleteError && (
            <Typography
              color='error'
              variant='body2'
              sx={{ mt: 1 }}
            >
              {deleteError}
            </Typography>
          )}
        </Box>
      </Modal>

      <Dialog
        open={isConfirmDeleteDialogOpen}
        onClose={() => setIsConfirmDeleteDialogOpen(false)}
        aria-labelledby='delete-confirm-dialog-title'
        aria-describedby='delete-confirm-dialog-description'
      >
        <DialogTitle id='delete-confirm-dialog-title'>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='delete-confirm-dialog-description'>
            Are you sure you want to delete these {count} transaction(s)? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            onClick={() => setIsConfirmDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            size='large'
            onClick={handleConfirmDelete}
            color='error'
            disabled={isDeleting}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkEditTransactionModal;
